import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import posthog from "posthog-js";
import { useTranslation, useLang } from "@/lib/i18n";

const CookieBanner = () => {
  const t = useTranslation();
  const { lang } = useLang();
  const prefersReduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = () => {
      if (typeof posthog.get_explicit_consent_status !== "function") return;
      setVisible(posthog.get_explicit_consent_status() === "pending");
    };
    check();
    const id = window.setTimeout(check, 600);
    const handleReopen = () => setVisible(true);
    window.addEventListener("cookie-banner-reopen", handleReopen);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("cookie-banner-reopen", handleReopen);
    };
  }, []);

  const accept = () => {
    posthog.opt_in_capturing();
    setVisible(false);
  };

  const reject = () => {
    posthog.opt_out_capturing();
    setVisible(false);
  };

  const privacyHref = lang === "pl" ? "/pl/privacy" : "/privacy";
  const b = t.legal.cookieBanner;

  return (
    <AnimatePresence>
      {visible && (
        <div
          className="fixed bottom-3 sm:bottom-4 inset-x-3 z-50 flex justify-center pointer-events-none"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <motion.div
            initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-xl pointer-events-auto rounded-2xl border border-border/60 bg-card/95 backdrop-blur-xl shadow-2xl p-4 sm:p-6"
          >
            <p className="text-xs sm:text-sm font-body text-foreground/90 leading-relaxed">
              {b.message}{" "}
              <Link
                to={privacyHref}
                className="underline underline-offset-2 hover:text-primary transition-colors"
              >
                {b.privacyLink}
              </Link>
              .
            </p>
            <div className="mt-3 sm:mt-4 flex flex-row gap-2 sm:gap-3">
              <button
                onClick={reject}
                className="flex-1 px-5 py-2.5 min-h-[44px] rounded-full border border-border/80 text-foreground/90 font-body font-medium text-sm hover:bg-foreground/5 transition-colors"
              >
                {b.reject}
              </button>
              <button
                onClick={accept}
                className="flex-1 px-5 py-2.5 min-h-[44px] rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-colors glow"
              >
                {b.accept}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
